import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { NumberCard } from "../../../components/cards/numberCard";
import { SearchInput } from "../../../components/inputs/searchInput";
import { Pill } from "../../../components/pill";
import { TableComponent } from "../../../components/table";
import { TabNav } from "../../../components/tabNav";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGetAllEventsQuery } from "../../../redux/features/admin/event/events.api";
import { GetAllEventsQuery } from "../../../redux/features/admin/event/events.types";
import { getTimeAgo } from "../../../utils/helpers";

export const EventsPage = () => {
  const [query, setQuery] = useState<GetAllEventsQuery>({
    page: 1,
    limit: 20,
    search: undefined,
    sortOrder: "asc",
    status: undefined,
    userType: undefined,
  });

  const navigate = useNavigate();

  const tabs = [
    {
      title: "All",
      to: "all",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: undefined,
        });
      },
    },
    {
      title: "Upcoming",
      to: "upcoming",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: "upcoming",
        });
      },
    },
    {
      title: "Past",
      to: "completed",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: "completed",
        });
      },
    },
  ];

  const {
    isFetching: fetchingEvents,
    isLoading: loadingEvents,
    data: eventsData,
  } = useGetAllEventsQuery(
    { ...query, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  return (
    <div className="w-full">
      <PageHeader header={"Events"} className="font-unbounded font-normal" />

      <TabNav
        navs={tabs}
        className="mt-8 !min-w-full md:!min-w-fit md:!max-w-fit"
        tabClassName=""
        activeTab={query.status ?? "all"}
        variant="switcher"
        theme="plain"
      />

      <div className="mt-3 flex flex-col gap-[2px]">
        <BgWrapper className="grid grid-cols-1 gap-[10px] rounded-b-none md:grid-cols-2">
          <NumberCard
            value={eventsData?.data?.stats?.upcoming ?? 0}
            text="Upcoming Events"
            isCurrency={false}
            loading={loadingEvents}
            titleFirst
          />

          <NumberCard
            value={eventsData?.data?.stats?.past ?? 0}
            text="Past Events"
            isCurrency={false}
            loading={loadingEvents}
            titleFirst
          />
        </BgWrapper>

        <BgWrapper className="rounded-t-none">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  if (query.sortOrder === "asc") {
                    setQuery({ ...query, sortOrder: "desc" });
                  } else {
                    setQuery({ ...query, sortOrder: "asc" });
                  }
                }}
                text="Date Modified"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={() => {}}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
              />
            </div>
          </div>

          <TableComponent
            headers={[]}
            rows={
              eventsData?.data?.events?.map((event, idx) => {
                return {
                  id: idx,
                  items: [
                    <div
                      className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                    >
                      <img
                        src={event.image}
                        alt="event banner"
                        className="block h-[40px] w-[40px] overflow-hidden rounded-sm object-cover"
                      />

                      <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                        <span className="font-bold text-grey-900">
                          {event.name}
                        </span>
                        <span className={` `}>∙</span>
                        <span
                          className={``}
                        >{`${event.totalTicketsSold} ticket${event.totalTicketsSold === 1 ? "" : "s"} sold`}</span>
                      </p>

                      <div className="flex flex-row flex-wrap items-center gap-2">
                        <Pill
                          text={
                            event.type === "physical" ? "PHYSICAL" : "VIRTUAL"
                          }
                          variation={
                            event.status === "completed"
                              ? "grey"
                              : event.type === "physical"
                                ? "blue"
                                : "purple"
                          }
                        />

                        {event.totalTicketsSold > 0 && (
                          <Pill
                            text={"PAID TICKET"}
                            variation={
                              event.status === "completed" ? "grey" : "green"
                            }
                          />
                        )}

                        <Pill
                          text={
                            event.status === "upcoming" ? "UPCOMING" : "PAST"
                          }
                          variation={
                            event.status === "completed" ? "grey" : "blue"
                          }
                        />

                        <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                          Created {getTimeAgo(event.createdAt)}
                        </p>
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            loadingRows={query.limit}
            isLoading={fetchingEvents}
            isRowClickable
            onRowClick={(idx) => {
              navigate(`/events/${eventsData?.data?.events[idx]?._id}`);
            }}
            isTableEmpty={[0, undefined].includes(
              eventsData?.data?.events?.length,
            )}
            emptyTitle="No Events"
            emptySubTitle=""
            emptyImage="/assets/images/calendar-3d.png"
            isPaginated
            currentPage={query.page ?? 1}
            totalCount={eventsData?.data?.stats?.total ?? 0}
            itemsPerPage={query.limit!}
            pageRangeDisplayed={3}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              });
            }}
            className="mt-3"
          />
        </BgWrapper>
      </div>
    </div>
  );
};
